<script setup>
import { ref, computed, inject, provide, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { mensagemDoErro } from '../erros';
import { normalizar } from '../busca';
import { useMapGetter } from 'dashboard/composables/store';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useMontaFlow } from 'dashboard/composables/useMontaFlow';
import { useGestorDoCofre } from 'dashboard/composables/useGestorDoCofre';
import powerAgentsApi from 'dashboard/api/powerAgents';
import toolBindingsApi from 'dashboard/api/toolBindings';
import { BUILTINS } from '../variableTokens';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import toolInstancesApi from 'dashboard/api/toolInstances';
import teamsApi from 'dashboard/api/teams';
import AddToolDialog from './AddToolDialog.vue';
import HandoffForm from './HandoffForm.vue';
import KanbanToolForm from './KanbanToolForm.vue';
import kanbanApi from 'dashboard/api/kanban';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import EmptyStateLayout from 'dashboard/components-next/EmptyStateLayout.vue';
import SectionShell from './SectionShell.vue';
import ToolsCrud from '../../agentShared/ToolsCrud.vue';
import VariablesPanel from './VariablesPanel.vue';
import IconeDaTool from './IconeDaTool.vue';
import SegredosDaConta from '../../powerTools/SegredosDaConta.vue';
import { criarSegredosDaConta } from '../../powerTools/flow/segredosDaConta';

const props = defineProps({
  agent: { type: Object, required: true },
});

const { t } = useI18n();
const ctx = inject('powerAgentContext');
const router = useRouter();
// FLOW-03b (Task 3.7; PLAN P13): o flow novo também leva o agente (`?agente=`): ele nasce de Agente,
// com o "Ligar a este agente".
const novoFlow = () =>
  router.push({
    name: 'powerflow_flow_novo',
    params: { accountId: ctx.accountId() },
    query: { agente: props.agent.slug },
  });
// FLOW-13 (SPEC S4, R12; PLAN G12): a ferramenta HTTP nasce na tela dela, e o agente de onde ela veio
// vai junto (`?agente=`), para o "Ligar a este agente".
const novaFerramentaHttp = () =>
  router.push({
    name: 'powerflow_ferramenta_http_nova',
    params: { accountId: ctx.accountId() },
    query: { agente: props.agent.slug },
  });
const currentUser = useMapGetter('getCurrentUser');

// Ferramentas aparece para o cliente e para a faixa de leitura desde 22/09/2026, SÓ PARA
// LEITURA: eles veem o que o agente sabe fazer, e o cliente configura o atendimento humano
// pelo card dele. Adicionar, tirar, ligar, desligar, as variáveis e o detalhe de cada tool
// (URL, cabeçalhos, provedor) continuam sendo da plataforma.
const { isSuperAdmin } = useAdmin();
// FLOW-11 (S4, Task 4.8): o administrador da conta monta flow, na camada dele (o servidor recusa o
// passo de fora). O atendente, que lê esta aba, continua sem "Novo flow" e sem "Abrir no editor".
// Deploy do PowerFlow (D2, 16/10): o administrador só monta com a chave da instalação ligada
// (`useMontaFlow`). Sem ela, não vê "Novo flow", "Nova ferramenta HTTP" nem "Abrir no editor" (emenda
// da FLOW-13 de 18/10: escondidos, e não mais desativados com "Em breve"); o flow continua na lista,
// como qualquer tool.
const { montaFlow } = useMontaFlow();
// O diálogo de atendimento humano é teleportado para fora do fieldset do SectionShell: o
// Salvar dele some aqui para a faixa de leitura, e os campos travam no próprio formulário.
const somenteLeitura = inject(
  'powerAgentSomenteLeitura',
  computed(() => false)
);

// FLOW-13 (SPEC S1, Q1, Q5; PLAN G1, G19): a sub-aba "Segredos" mostra o cofre da conta, o mesmo em
// todos os agentes, ao gestor do cofre: o super-admin e, com a flag PowerAgents, o administrador e o
// `poweragents_manage`, sem depender da chave do PowerFlow. O atendente não a vê.
const { gestorDoCofre } = useGestorDoCofre();

// Sub-abas: Ferramentas, Variáveis (só o super-admin) e Segredos (o gestor do cofre).
//
// Variáveis só servem para alimentar tool ({{name}} em URL/header), então moram aqui em vez de
// disputar espaço na navegação do agente. O CRUD antigo desceu para uma aba ROTULADA de legado
// porque ele edita `url_template`/`http_method`/`headers` — campos que só existem no catálogo
// antigo. Deixá-lo no meio da página fazia parecer que era ali que se cria qualquer tool, e a
// primeira coisa que alguém tentava era criar uma tool nativa num formulário que pede URL.
// A aba 'legacy' saiu em TOOLS-05: as tools do catálogo antigo já apareciam na lista única,
// então a aba as mostrava DE NOVO — e era o único lugar onde dava para editá-las. A edição
// passou para o diálogo, que serve aos dois catálogos.
// O TabBar é CONTROLADO: ele não guarda índice nenhum, emite o objeto da aba clicada e espera
// que o pai mande o `initial-active-tab` novo. Tratá-lo como não-controlado deixa o indicador
// parado na primeira aba enquanto o conteúdo troca. O índice é o da aba na lista que a pessoa vê, e
// não numa lista fixa: o gestor que não é super-admin vê Ferramentas e Segredos, sem Variáveis.
const subtab = ref('tools');

// O gestor mexe nos segredos mesmo com o resto da aba em leitura (SPEC S1): na sub-aba Segredos, a
// faixa de leitura não desliga os controles. As sub-abas ficam fora do fieldset (`#abas` do
// `SectionShell`), para quem lê o agente conseguir chegar a ela.
provide(
  'powerAgentSomenteLeitura',
  computed(() => somenteLeitura.value && subtab.value !== 'segredos')
);

// Os nomes do cofre para a caixa de variáveis do formulário da tool antiga, que acha o `{{nome}}` no
// cofre antes do agente (R2). Só quem abre o formulário (o super-admin) os carrega, ao abrir.
const cofre = criarSegredosDaConta({ accountId: () => ctx.accountId() });
const nomesDoCofre = cofre.nomes;

// System built-ins + the agent's declared context variables, insertable as {{name}}
// tokens into any tool value field.
const toolVariables = computed(() => [
  ...BUILTINS.map(name => ({ name, kind: 'system' })),
  ...(props.agent.config?.context_variables || []).map(v => ({
    name: v.name,
    kind: 'declared',
  })),
]);

// A busca em base NÃO aparece aqui, e some por dois motivos que se somam.
//
// Ela não é escolha: existe porque o agente tem base conectada e some quando a última sai
// (`PowerAgents::KnowledgeBindingSync`). Mostrá-la entre as tools sugere que dá para
// removê-la — e removê-la aqui só faria a próxima sincronização trazê-la de volta.
//
// E o que ela mostrava estava errado: a descrição semeada no catálogo diz "Search the agent
// knowledge base (ChatData)", e o ChatData saiu do caminho há tempo — o publish reescreve a
// tool para a busca da PowerData. Quem lia a aba Tools via o provedor errado.
//
// O lugar dela é a aba Knowledge, que mostra as bases de verdade e avisa em âmbar quando não
// há nenhuma.
const TOOL_DE_BUSCA = 'query_knowledge';
const semABusca = lista =>
  (lista || []).filter(b => b?.tool?.name !== TOOL_DE_BUSCA);

const bindings = ref(semABusca(props.agent.tools));
const availableTools = ref([]);
const loading = ref(false);

// ── Pedir atendimento humano ──────────────────────────────────
//
// A tool de pedir humano NÃO é vínculo de catálogo: o runtime a monta a partir de
// `config.handoff.tool_solicitar_humano`, e o vínculo nunca foi consultado. Por isso o card
// dela nasce da config — e aparece em todo agente, porque ausente é ligada — e fica fora da
// lista paginada: não vem do catálogo, não sai do agente e não pagina.
//
// Desde 22/09/2026 ela é também o lugar da configuração inteira do atendimento humano: a aba
// saiu, e o clique no card abre o diálogo com o que ela liga (decisão do Murilo).
const handoff = computed(() => props.agent.config?.handoff || {});
const handoffLigada = computed(
  () => handoff.value.tool_solicitar_humano?.ativa !== false
);
const teams = ref([]);

async function carregarTimes() {
  try {
    const { data } = await teamsApi.get();
    teams.value = data || [];
  } catch (e) {
    // Sem a lista, o card mostra o id do time: ainda é mais verdadeiro do que esconder a linha.
    teams.value = [];
  }
}

function nomeDoTime(id) {
  return (
    teams.value.find(team => String(team.id) === String(id))?.name || `#${id}`
  );
}

// A segunda linha do card: o que a tool liga, lido da MESMA config que o diálogo edita. Sem
// time, o pedido abre a conversa na fila geral da caixa — o card escreve isso por extenso,
// porque "sem time" soava como encaminhar para lugar nenhum.
const resumoDoHandoff = computed(() => {
  if (!handoffLigada.value) return t('POWER_AGENTS.HANDOFF.CARD_OFF');
  const tool = handoff.value.tool_solicitar_humano || {};
  const padrao = tool.roteamento?.padrao;
  const categorias = tool.categorias || [];
  const partes = [];
  if (categorias.length) {
    partes.push(
      t(
        'POWER_AGENTS.HANDOFF.CARD_CATEGORIES',
        { count: categorias.length },
        categorias.length
      )
    );
    partes.push(
      padrao
        ? t('POWER_AGENTS.HANDOFF.CARD_REST_TEAM', { team: nomeDoTime(padrao) })
        : t('POWER_AGENTS.HANDOFF.CARD_REST_QUEUE')
    );
  } else {
    partes.push(
      padrao
        ? t('POWER_AGENTS.HANDOFF.CARD_TEAM', { team: nomeDoTime(padrao) })
        : t('POWER_AGENTS.HANDOFF.CARD_QUEUE')
    );
  }
  if (tool.resumo_ao_encaminhar?.ativo) {
    partes.push(t('POWER_AGENTS.HANDOFF.CARD_WITH_SUMMARY'));
  }
  // Escalar para ninguém o runtime desliga sozinho; o card não promete o que não acontece.
  if (tool.escalonamento?.ativo && tool.escalonamento?.para) {
    partes.push(
      t('POWER_AGENTS.HANDOFF.CARD_ESCALATES', {
        min: tool.escalonamento.apos_minutos ?? 30,
      })
    );
  }
  return partes.join(' · ');
});

// O switch escreve SÓ `ativa`, dentro do bloco inteiro: o merge do servidor é raso, e mandar
// `handoff` sem o resto apagaria a configuração que o diálogo guarda.
async function alternarHandoff() {
  const atual = props.agent.config?.handoff || {};
  try {
    const updated = await powerAgentsApi.update(
      ctx.accountId(),
      ctx.agentId(),
      {
        config: {
          handoff: {
            ...atual,
            tool_solicitar_humano: {
              ...(atual.tool_solicitar_humano || {}),
              ativa: !handoffLigada.value,
            },
          },
        },
      }
    );
    ctx.onSectionSaved(updated);
    // Handoff vale na revisão publicada, como toda config: o card muda na hora, o agente só
    // depois do Publish — e a pílula de pendente é o que diz isso.
    ctx.markPending();
  } catch (e) {
    useAlert(mensagemDoErro(e, t, t('POWER_AGENTS.TOOLS.ERROR')));
  }
}

const handoffDialogRef = ref(null);
const handoffFormRef = ref(null);

function abrirHandoff() {
  handoffDialogRef.value?.open();
}

// ── Atualizar o funil (CRM-03) ────────────────────────────────
//
// Como o pedido de humano, a tool não é vínculo de catálogo: o runtime a monta de
// `config.kanban`. Diferenças que importam: só aparece em conta com a flag `kanban` (sem ela
// não há funil nenhum para mover, e o publish recusa ligar) e AUSENTE É DESLIGADA — a tool
// escreve em dado de negócio, então ninguém a herda sem alguém da plataforma ligar.
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);
const kanbanNaConta = computed(() =>
  Boolean(
    isFeatureEnabledonAccount.value?.(ctx.accountId(), FEATURE_FLAGS.KANBAN)
  )
);
const kanban = computed(() => props.agent.config?.kanban || {});
const kanbanLigada = computed(() => kanban.value.ativa === true);
const funis = ref([]);

async function carregarFunis() {
  try {
    const { data } = await kanbanApi.getPipelines();
    funis.value = data?.payload || [];
  } catch (e) {
    // Sem a lista, o card mostra o id do funil: ainda diz o que está configurado.
    funis.value = [];
  }
}

// A segunda linha do card: funil e quantas etapas o agente alcança, lidos da MESMA config
// que o diálogo edita.
const resumoDoKanban = computed(() => {
  if (!kanbanLigada.value) return t('POWER_AGENTS.KANBAN.CARD_OFF');
  const id = kanban.value.pipeline_id;
  if (!id) return t('POWER_AGENTS.KANBAN.CARD_NO_PIPELINE');
  const nome =
    funis.value.find(funil => String(funil.id) === String(id))?.name ||
    `#${id}`;
  const count = (kanban.value.etapas || []).length;
  return t(
    'POWER_AGENTS.KANBAN.CARD_SUMMARY',
    { pipeline: nome, count },
    count
  );
});

// O switch escreve SÓ `ativa`, dentro do bloco inteiro: o merge do servidor é raso.
async function alternarKanban() {
  try {
    const updated = await powerAgentsApi.update(
      ctx.accountId(),
      ctx.agentId(),
      { config: { kanban: { ...kanban.value, ativa: !kanbanLigada.value } } }
    );
    ctx.onSectionSaved(updated);
    ctx.markPending();
  } catch (e) {
    useAlert(mensagemDoErro(e, t, t('POWER_AGENTS.TOOLS.ERROR')));
  }
}

const kanbanDialogRef = ref(null);
const kanbanFormRef = ref(null);

function abrirKanban() {
  kanbanDialogRef.value?.open();
}

// TOOLS-01: o catálogo NOVO, em lista própria.
//
// Não entra em `bindings` de propósito. O CRUD da aba de legado foi escrito para tool `declared`
// do catálogo antigo — ele mostra e edita url_template, http_method e headers. Uma tool `native`
// não tem nada disso: ela é `impl` em Python, executada no runtime. Misturar as duas na EDIÇÃO
// faria a tela inventar uma URL ou exibir três campos vazios, e o operador leria os dois como
// defeito.
const catalogo = ref([]);
const disponiveis = ref([]);
// Separa "não há o que anexar" de "não consegui perguntar o que há" — as duas davam a
// mesma tela antes, e só uma delas é o operador não ter mais nada a fazer.
const ligando = ref(false);

async function fetchTools() {
  loading.value = true;
  try {
    const data = await powerAgentsApi.toolsIndex(
      ctx.accountId(),
      ctx.agentId()
    );
    bindings.value = semABusca(data.bindings);
    availableTools.value = data.available_tools || [];
    catalogo.value = data.catalogo || [];
  } catch (e) {
    useAlert(mensagemDoErro(e, t, t('POWER_AGENTS.TOOLS.ERROR')));
  } finally {
    loading.value = false;
  }
}

async function carregarDisponiveis() {
  try {
    const data = await toolBindingsApi.index(ctx.accountId(), props.agent.slug);
    disponiveis.value = data.disponiveis || [];
  } catch (e) {
    // Continua sem alerta: a LISTA do catálogo já carregou e é o que importa na tela, e um
    // toast aqui faria parecer que a aba quebrou. Mas o silêncio não pode chegar até o
    // BOTÃO: com `v-if="disponiveis.length"`, a falha deste fetch fazia o único caminho
    // para anexar tool desaparecer, e a tela ficava idêntica a "não há mais nada para
    // anexar". Quem opera não tinha como distinguir as duas.
    disponiveis.value = [];
  }
}

// As instâncias de template da conta — o "provedor" do diálogo. A conta não CRIA instância
// (isso é do provisionador); aqui elas só são lidas, e a credencial é preenchida por
// `PATCH .../secrets`, que é o único caminho de conta que existe.
const instancias = ref([]);

async function carregarInstancias() {
  try {
    const data = await toolInstancesApi.index(ctx.accountId());
    instancias.value = data.payload || [];
  } catch (e) {
    // Sem alerta: o diálogo ainda serve para as tools da plataforma e para criar a sua. Um toast
    // aqui faria parecer que a seção quebrou por causa de um provedor que a conta talvez nem tenha.
    instancias.value = [];
  }
}

// O diálogo devolve TODAS as marcadas de uma vez. Em série, e não em paralelo: `position` é
// calculada a partir do maior vínculo existente, e dois POSTs simultâneos disputariam o mesmo
// número — duas tools na mesma posição, com a ordem decidida por quem gravar por último.
// FLOW-13 (aceite 7.6): o vínculo do catálogo só chega ao agente na publicação dele (PLAN, contexto), e
// o Rails conta os vínculos no que há a publicar (`51c1219c2`): anexar, desanexar e ligar ou desligar
// acendem o "pendente" do cabeçalho na hora, como as outras edições da aba, sem recarregar.
async function anexarVarias(ids) {
  ligando.value = true;
  let anexadas = 0;
  try {
    // eslint-disable-next-line no-restricted-syntax
    for (const id of ids) {
      // eslint-disable-next-line no-await-in-loop
      await toolBindingsApi.create(ctx.accountId(), props.agent.slug, id);
      anexadas += 1;
    }
    await Promise.all([fetchTools(), carregarDisponiveis()]);
    useAlert(
      t('POWER_AGENTS.TOOLS.ADD_ADDED', { count: ids.length }, ids.length)
    );
  } catch (e) {
    useAlert(mensagemDoErro(e, t, t('POWER_AGENTS.TOOLS.ERROR')));
  } finally {
    // A que já entrou antes de uma falha também espera a publicação.
    if (anexadas) ctx.markPending();
    ligando.value = false;
  }
}

const formRef = ref(null);
const formDialogRef = ref(null);
const cartaEmEdicao = ref(null);

function abrirFormulario(carta) {
  cartaEmEdicao.value = carta || null;
  // A cada abertura: um segredo criado na sub-aba ao lado já aparece na caixa de variáveis.
  if (gestorDoCofre.value) cofre.recarregar();
  formDialogRef.value?.open();
  // O componente do formulário só existe depois que o diálogo monta o conteúdo.
  requestAnimationFrame(() => {
    if (carta) formRef.value?.openEditForm(carta.toolOriginal);
    else formRef.value?.openForm();
  });
}

// O diálogo de anexar, no lugar do <select>. Busca por nome E por descrição: quem procura
// "cep" não sabe que a tool se chama `consultar_cep`, e quem procura "humano" não sabe que
// a descrição é que tem a palavra.
const dialogoRef = ref(null);

// FLOW-13 (S4): "Ferramenta HTTP" no "Adicionar ferramenta" abre a tela nova, e não o formulário
// antigo, que continua só para editar a tool antiga que já existe.
function abrirCriacao() {
  dialogoRef.value?.close();
  novaFerramentaHttp();
}

async function desligarTool(bindingId) {
  try {
    await toolBindingsApi.destroy(ctx.accountId(), bindingId);
    ctx.markPending();
    await Promise.all([fetchTools(), carregarDisponiveis()]);
    useAlert(t('POWER_AGENTS.TOOLS.CATALOG_DETACHED'));
  } catch (e) {
    useAlert(mensagemDoErro(e, t, t('POWER_AGENTS.TOOLS.ERROR')));
  }
}

// #4: awaited by ToolsCrud so the form only closes on success. On failure we
// surface the backend's specific message (e.g. duplicate name) by re-throwing —
// the draft stays intact and the error renders inline, not as a generic toast.
async function bindCustom(payload) {
  try {
    await powerAgentsApi.toolsCreate(ctx.accountId(), ctx.agentId(), payload);
    ctx.markPending();
    useAlert(t('POWER_AGENTS.TOOLS.CREATED'));
    fetchTools();
  } catch (e) {
    const backendError = e?.response?.data?.errors?.[0];
    throw new Error(backendError || t('POWER_AGENTS.TOOLS.ERROR'));
  }
}

// #11: edit an existing custom tool (catalog update). The definition change is structural →
// feeds the Publish pill. Re-throws the backend message so ToolsCrud shows it inline.
async function updateTool(toolId, toolData) {
  try {
    await powerAgentsApi.catalogUpdate(ctx.accountId(), toolId, toolData);
    ctx.markPending();
    useAlert(t('POWER_AGENTS.TOOLS.UPDATED'));
    fetchTools();
  } catch (e) {
    const backendError =
      e?.response?.data?.errors?.[0] || e?.response?.data?.error;
    throw new Error(backendError || t('POWER_AGENTS.TOOLS.ERROR'));
  }
}

// #11: delete a custom tool from the catalog. The backend refuses built-ins and in-use tools.
async function deleteTool(toolId) {
  try {
    await powerAgentsApi.catalogDestroy(ctx.accountId(), toolId);
    useAlert(t('POWER_AGENTS.TOOLS.DELETED'));
    fetchTools();
  } catch (e) {
    const backendError =
      e?.response?.data?.errors?.[0] || e?.response?.data?.error;
    useAlert(backendError || t('POWER_AGENTS.TOOLS.ERROR'));
  }
}

// Per-user, per-agent draft key so an interrupted custom tool survives errors/refresh (#4)
// without a shared-browser co-admin ever loading someone else's draft.
const draftKey = computed(
  () =>
    `na-tool-draft-${currentUser.value?.id || 0}-${ctx.accountId()}-${ctx.agentId()}`
);

async function unbind(bindingId) {
  try {
    await powerAgentsApi.toolsDestroy(
      ctx.accountId(),
      ctx.agentId(),
      bindingId
    );
    ctx.markPending();
    useAlert(t('POWER_AGENTS.TOOLS.REMOVED'));
    fetchTools();
  } catch (e) {
    useAlert(mensagemDoErro(e, t, t('POWER_AGENTS.TOOLS.ERROR')));
  }
}

// Dry-run a tool against sample params before saving (#3b). Server resolves any
// secrets, so nothing sensitive round-trips through the client.
async function runTest(payload) {
  return powerAgentsApi.toolTest(ctx.accountId(), ctx.agentId(), payload);
}

// Toggling `enabled` is a value change (🟢), not structural.
async function toggle(bindingId, enabled) {
  try {
    await powerAgentsApi.toolsUpdate(
      ctx.accountId(),
      ctx.agentId(),
      bindingId,
      {
        enabled,
      }
    );
    const idx = bindings.value.findIndex(b => b.id === bindingId);
    if (idx >= 0) bindings.value[idx].enabled = enabled;
  } catch (e) {
    useAlert(mensagemDoErro(e, t, t('POWER_AGENTS.TOOLS.ERROR')));
  }
}

// O nome de função é contrato com o MODELO, não texto de tela: `solicitar_humano` diz ao
// modelo qual ferramenta chamar. Quem opera não precisa lê-lo para decidir se liga a tool —
// precisa saber o que ela faz, e isso já está na `description`. Humanizar o nome (em vez de
// um mapa de rótulos escrito à mão) vale para toda tool futura, inclusive as que ainda não
// existem; mapa à mão deixaria cada tool nova sem rótulo e ninguém perceberia.
function humanizar(nome) {
  const cru = String(nome || '')
    .replace(/[_-]+/g, ' ')
    .trim();
  return cru ? cru.charAt(0).toUpperCase() + cru.slice(1) : '';
}

// A ORIGEM é a pergunta que decide o que se pode fazer com a tool, e por isso é ela que agrupa.
// Uma tool da conta se edita; uma compartilhada, não (o backend recusa); uma de legado se edita
// no formulário antigo, que é outra aba. Agrupar por origem põe cada uma perto das que se
// comportam igual — e é o que o selo `global`, destravado nesta spec, finalmente permite.
const ORIGEM_CONTA = 'account';
const ORIGEM_COMPARTILHADA = 'shared';
const ORIGEM_LEGADO = 'legacy';

// Uma lista só na TELA, duas no modelo. O que se funde aqui é a LEITURA: quem opera pergunta
// "o que este agente sabe fazer?", e essa pergunta não tem duas respostas. A EDIÇÃO segue
// separada — e é por isso que o detalhe de cada carta difere por origem.
const cartas = computed(() => [
  ...catalogo.value.map(item => ({
    chave: `cat-${item.binding_id}`,
    id: item.binding_id,
    doCatalogo: true,
    origem: item.global ? ORIGEM_COMPARTILHADA : ORIGEM_CONTA,
    forma: item.kind,
    // FLOW-13 (S4, R8): o Rails diz a forma do flow (`forma`: `ferramenta_http` | `flow`), sem mandar o
    // YAML; a ferramenta HTTP leva o globo (mockup aprovado, decisão 5).
    ferramentaHttp: item.forma === 'ferramenta_http',
    estado: item.status,
    titulo: humanizar(item.name),
    nome: item.name,
    versao: item.version,
    versionSpec: item.version_spec,
    descricao: item.description,
    notaDeUso: item.usage_note,
    instanciaId: item.instance_id,
    global: item.global,
    ligada: item.enabled !== false,
    // `declared` mostra para onde vai; `native` e `template` mostram o que executa. São
    // perguntas diferentes, e a tela precisa das duas para não parecer que falta informação.
    detalhe:
      item.kind === 'declared' ? (item.upstreams || []).join(', ') : item.impl,
    parametros: item.parametros || [],
    // FLOW-01 (task 5.5): o caminho do flow para o editor. A cadeia dos passos, que vinha em chips
    // coloridos por família, saiu da carta em 16/10 (pedido do Murilo: nada de etiqueta colorida).
    entradaId: item.id,
  })),
  ...bindings.value.map(b => ({
    chave: `old-${b.id}`,
    id: b.id,
    doCatalogo: false,
    origem: ORIGEM_LEGADO,
    forma: ORIGEM_LEGADO,
    estado: '',
    titulo: humanizar(b.tool?.name),
    nome: b.tool?.name,
    versao: null,
    versionSpec: '',
    descricao: b.tool?.description,
    notaDeUso: '',
    instanciaId: null,
    global: false,
    ligada: b.enabled !== false,
    detalhe: b.tool?.url_template,
    // A linha crua, para o formulário de edição abrir com ela. A carta é a LEITURA; editar
    // precisa do registro como o servidor o devolveu.
    toolOriginal: b.tool,
    parametros: [],
  })),
]);

// ── Busca e filtros ───────────────────────────────────────────
//
// A busca reusa o `normalizar` do índice da seção em vez de um `toLowerCase()` próprio: lá ele
// também tira acento (NFD), e é isso que faz "manutencao" achar "manutenção". Duas normalizações
// diferentes na mesma tela é como a busca de cima acha e a de baixo não, sem nada explicar.
//
// Procura em `name`, `description` E `usage_note`. A nota de uso é o campo que separa duas tools
// parecidas ("use esta quando o cliente já tiver pedido"), e quem procura "estoque" muitas vezes
// está procurando exatamente a frase que só existe lá.
const busca = ref('');
const termo = computed(() => normalizar(busca.value).trim());

// TOOLS-05: as quatro fileiras de filtro saíram — Effect, Shape, Origin e State. Todas dividiam
// por vocabulário de sistema, e nenhuma respondia à pergunta de quem opera ("o que este agente
// sabe fazer?"). Sobrou a busca, que procura em nome, descrição e nota de uso.
const temBusca = computed(() => !!termo.value);

function combinaComABusca(carta) {
  if (termo.value.length < 2) return true;
  const alvo = normalizar(
    `${carta.nome || ''} ${carta.descricao || ''} ${carta.notaDeUso || ''}`
  );
  return alvo.includes(termo.value);
}

const filtradas = computed(() => cartas.value.filter(combinaComABusca));

// O card de pedir humano passa pela MESMA busca: quem procura "humano" ou "pessoa" tem de
// achá-lo, e quem procura "cep" não deve vê-lo sobrando no topo da lista.
const handoffNaBusca = computed(() => {
  if (termo.value.length < 2) return true;
  return normalizar(
    `solicitar_humano ${t('POWER_AGENTS.HANDOFF.TOOL_NAME')} ${t('POWER_AGENTS.HANDOFF.TOOL_DESCRIPTION')}`
  ).includes(termo.value);
});

// O card do funil passa pela mesma busca, e só existe com a flag.
const kanbanNaBusca = computed(() => {
  if (!kanbanNaConta.value) return false;
  if (termo.value.length < 2) return true;
  return normalizar(
    `atualizar_funil kanban ${t('POWER_AGENTS.KANBAN.TOOL_NAME')} ${t('POWER_AGENTS.KANBAN.TOOL_DESCRIPTION')}`
  ).includes(termo.value);
});

// ── Paginação ─────────────────────────────────────────────────
//
// Só acima de 50 itens. Abaixo disso, rolar é mais barato do que paginar, e um rodapé de
// paginação com "1 de 1 página" só ocupa espaço e sugere que há mais coisa escondida.
const POR_PAGINA = 50;
const paginaAtual = ref(1);
const paginado = computed(() => filtradas.value.length > POR_PAGINA);
const visiveis = computed(() => {
  if (!paginado.value) return filtradas.value;
  const inicio = (paginaAtual.value - 1) * POR_PAGINA;
  return filtradas.value.slice(inicio, inicio + POR_PAGINA);
});

// Filtrar de volta para a página 1. Sem isto, filtrar estando na página 3 deixa a lista vazia
// com o rodapé dizendo "3 de 1" — e a tela fica indistinguível de "nada casa".
watch(termo, () => {
  paginaAtual.value = 1;
});

const abas = computed(() => [
  {
    key: 'tools',
    label: t('POWER_AGENTS.SECTIONS.TOOLS'),
    // +1: o card de Pedir atendimento humano está sempre na lista, fora de `cartas` porque
    // não é do catálogo. Sem ele a aba dizia 2 com 3 itens na tela.
    count: cartas.value.length + 1,
    attrs: { 'data-subaba': 'tools' },
  },
  // Variáveis só servem para montar URL e cabeçalho de tool: superfície de plataforma.
  ...(isSuperAdmin.value
    ? [
        {
          key: 'variables',
          label: t('POWER_AGENTS.SECTIONS.VARIABLES'),
          attrs: { 'data-subaba': 'variables' },
        },
      ]
    : []),
  ...(gestorDoCofre.value
    ? [
        {
          key: 'segredos',
          label: t('POWER_AGENTS.SECTIONS.ACCOUNT_SECRETS'),
          attrs: { 'data-subaba': 'segredos' },
        },
      ]
    : []),
]);

const subtabIndex = computed(() =>
  Math.max(
    0,
    abas.value.findIndex(aba => aba.key === subtab.value)
  )
);

function trocarSubtab(aba) {
  subtab.value = aba.key;
}

// O detalhe virou DIÁLOGO. Ele era um bloco que abria dentro da carta, e com ele vinham o
// campo de versão e o jargão de runtime — quatro tools e a lista não cabia na tela. Clicar na
// carta abre a caixa; a lista fica sendo a resposta de "o que este agente sabe fazer".
const detalheDialogRef = ref(null);
const cartaDetalhe = ref(null);

function abrirDetalhe(carta) {
  // Tool que alguém configurou vai DIRETO para o formulário: entrar numa caixa para clicar em
  // "editar" e abrir outra caixa é um degrau que não decide nada.
  if (!carta.doCatalogo) {
    abrirFormulario(carta);
    return;
  }
  cartaDetalhe.value = carta;
  detalheDialogRef.value?.open();
}

function removerDoAgente(carta) {
  detalheDialogRef.value?.close();
  if (carta.doCatalogo) desligarTool(carta.id);
  else unbind(carta.id);
}
// Liga/desliga sem desanexar. O PATCH já existia no controller (`enabled`, `position`,
// `version_spec`) e o cliente `toolBindings.update` já existia no front — nunca chamado.
// Era isto o "não dá para alterar nada": a tela não oferecia o que o backend já aceitava.
async function alternarLigada(carta) {
  const alvo = !carta.ligada;
  try {
    if (carta.doCatalogo) {
      await toolBindingsApi.update(ctx.accountId(), carta.id, {
        enabled: alvo,
      });
      const i = catalogo.value.findIndex(c => c.binding_id === carta.id);
      if (i >= 0) catalogo.value[i].enabled = alvo;
    } else {
      await toggle(carta.id, alvo);
    }
    ctx.markPending();
  } catch (e) {
    useAlert(mensagemDoErro(e, t, t('POWER_AGENTS.TOOLS.ERROR')));
  }
}

// O `version_spec` é o CANAL que o vínculo segue, e é o que o RESOLVEDOR lê — não a versão da
// entrada. Era editável pela API desde a TOOLS-03 e não tinha campo em lugar nenhum: mover um
// agente para a v2 exigia um UPDATE à mão, que é de onde o
// DEFEITO-VINCULO-APONTA-V2-E-SERVE-V1 nasceu.
//
// A config da loja (e a credencial dela) mora na INSTÂNCIA, não na entrada — rotacionar a chave
// é tocar naquela linha, e não nas N tools do template. Sem este link, quem via uma tool de
// template na aba do agente não tinha como chegar ao lugar onde ela se configura.
const rotaDaInstancia = computed(() => ({
  name: 'powerflow_ferramentas',
  params: { accountId: ctx.accountId() },
}));

onMounted(() => {
  fetchTools();
  carregarTimes();
  if (kanbanNaConta.value) carregarFunis();
  // O que se pode anexar e os provedores da conta só servem a quem anexa.
  if (isSuperAdmin.value) {
    carregarDisponiveis();
    carregarInstancias();
  }
});
</script>

<template>
  <SectionShell
    data-busca="tools"
    :title="t('POWER_AGENTS.TOOLS.TITLE')"
    :description="
      subtab !== 'tools'
        ? ''
        : isSuperAdmin
          ? t('POWER_AGENTS.TOOLS.DESCRIPTION')
          : t('POWER_AGENTS.TOOLS.DESCRIPTION_READONLY')
    "
  >
    <template #abas>
      <TabBar
        :tabs="abas"
        :initial-active-tab="subtabIndex"
        @tab-changed="trocarSubtab"
      />
    </template>

    <VariablesPanel
      v-if="subtab === 'variables'"
      data-busca="variables"
      :agent="agent"
      @ir-para-segredos="subtab = 'segredos'"
    />

    <!-- O cofre da conta, o mesmo da seção Cofre da aba PowerFlow, sem o título: a sub-aba já o diz. -->
    <SegredosDaConta v-if="subtab === 'segredos'" data-busca="segredos" />

    <!-- UMA LISTA SÓ na tela, agrupada por ORIGEM. O modelo continua com dois catálogos — e
         continua certo que continue: tool nativa não tem url_template/http_method/headers, e o
         CRUD de legado edita exatamente isso. O que se funde aqui é a LEITURA, porque quem opera
         faz uma pergunta só ("o que este agente sabe fazer?") e ela não tem duas respostas. -->
    <div v-if="subtab === 'tools'" class="flex flex-col gap-3">
      <!-- No celular o botão desce para baixo do texto: lado a lado, ele espremia o título
           numa coluna de três palavras por linha. -->
      <div
        class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between"
      >
        <div>
          <h4 class="text-sm font-medium text-n-slate-12">
            {{ t('POWER_AGENTS.TOOLS.LIST_TITLE') }}
          </h4>
          <p class="text-sm text-n-slate-11">
            {{ t('POWER_AGENTS.TOOLS.LIST_HINT') }}
          </p>
        </div>
        <!-- Sempre visível para a plataforma. Antes ele sumia quando não havia o que anexar —
             e a tela ficava idêntica à de quem não consegue anexar por falha de rede. Agora o
             botão também abre o caminho de CRIAR, que existe mesmo com o catálogo vazio. O
             cliente não o vê: anexar tool é da plataforma. -->
        <Button
          v-if="isSuperAdmin"
          data-busca="tools-add"
          variant="faded"
          color="slate"
          size="sm"
          icon="i-lucide-plus"
          class="self-start shrink-0"
          :label="t('POWER_AGENTS.TOOLS.ADD_TITLE')"
          @click="dialogoRef?.open()"
        />
        <!-- FLOW-01 (task 5.5): o flow nasce no editor. Depois de publicado, ele entra no agente pelo
             "Adicionar ferramenta", como qualquer tool. FLOW-11 (S4): o administrador também o monta.
             D2 do deploy, com a emenda da FLOW-13 de 18/10 (Murilo): sem a chave da instalação, o
             administrador não vê os dois botões. Antes, ele os via desativados, com "Em breve". -->
        <div
          v-if="montaFlow"
          class="flex items-center self-start gap-2 shrink-0"
        >
          <!-- FLOW-13 (S4): quem monta flow mas não vê "Adicionar ferramenta" cria a ferramenta HTTP
               daqui, ao lado do "Novo flow", com o mesmo portão (`useMontaFlow`). -->
          <Button
            v-if="!isSuperAdmin"
            size="sm"
            variant="faded"
            color="slate"
            icon="i-lucide-globe"
            data-nova-ferramenta-http
            :label="t('POWER_AGENTS.TOOLS.NEW_HTTP_TOOL')"
            @click="novaFerramentaHttp"
          />
          <Button
            size="sm"
            icon="i-lucide-workflow"
            data-novo-flow
            :label="t('POWER_AGENTS.TOOLS.NEW_FLOW')"
            @click="novoFlow"
          />
        </div>
      </div>

      <!-- O `Input` do components-next já nasce com `reset-base`, que é a saída da regra legada
           de `_base.scss` — a mesma que empurrou a lupa para cima do texto na busca de agentes e
           custou um ciclo inteiro de imagem para ser encontrada. A lupa entra pelo slot `prefix`,
           que existe para isso. -->
      <Input
        v-if="cartas.length"
        v-model="busca"
        data-busca="tools-search"
        size="sm"
        class="w-full"
        custom-input-class="ltr:!pl-8 rtl:!pr-8"
        :placeholder="t('POWER_AGENTS.TOOLS.CATALOG_SEARCH')"
      >
        <template #prefix>
          <span
            class="absolute z-10 top-2 i-lucide-search size-4 text-n-slate-10 ltr:left-2.5 rtl:right-2.5"
          />
        </template>
      </Input>

      <!-- Vazio de verdade e "nada casa com o filtro" são telas DIFERENTES: uma pede que se
           anexe uma tool, a outra pede que se limpe o filtro. Dar a mesma mensagem às duas faz
           quem filtrou concluir que o agente não tem tool nenhuma. -->
      <!-- "Este agente só conversa" deixou de ser verdade quando o card de pedir humano
           passou a aparecer em todo agente: o vazio só vale se ele também sumiu da busca. -->
      <EmptyStateLayout
        v-if="!filtradas.length && !handoffNaBusca && !kanbanNaBusca"
        :show-backdrop="false"
        :title="
          temBusca
            ? t('POWER_AGENTS.TOOLS.CATALOG_NO_MATCH_TITLE')
            : t('POWER_AGENTS.TOOLS.CATALOG_EMPTY_TITLE')
        "
        :subtitle="
          temBusca
            ? t('POWER_AGENTS.TOOLS.CATALOG_NO_MATCH_HINT')
            : t('POWER_AGENTS.TOOLS.LIST_EMPTY')
        "
      />

      <!-- Uma lista só, sem cabeçalho de origem. Agrupar por "de onde a tool veio" era o mesmo
           eixo dos filtros que saíram: diz qual sistema a construiu, e não o que ela faz. -->
      <div class="flex flex-col gap-2">
        <!-- Pedir atendimento humano: mesma anatomia das outras cartas, mais uma linha com o
             que ela liga. Fica fora do <ul> porque não é do catálogo e não pagina.

             Os gatilhos são <a role="button"> e não <button> DE PROPÓSITO: o <fieldset
             disabled> da faixa de leitura desliga todo botão aninhado, e o papel `agent`
             perderia até a LEITURA da configuração, que ele tinha na aba. O diálogo trava os
             campos por conta própria. O `py-1` é o que o `_base.scss` dá a todo <button>: sem
             ele esta carta ficava mais baixa que as outras. O resumo de baixo começa em 42 px
             (o ícone de 30 e o vão de 12), na coluna do texto. -->
        <div
          v-if="handoffNaBusca"
          data-busca="handoff-tool"
          class="border rounded-lg border-n-weak"
          :class="handoffLigada ? '' : 'opacity-60'"
        >
          <div class="flex items-center gap-3 p-3">
            <IconeDaTool />
            <a
              href="#"
              role="button"
              class="flex-1 min-w-0 py-1 text-start"
              @click.prevent="abrirHandoff"
            >
              <span class="block text-sm font-medium truncate text-n-slate-12">
                {{ t('POWER_AGENTS.HANDOFF.TOOL_NAME') }}
              </span>
              <span class="block text-xs truncate text-n-slate-11">
                {{ t('POWER_AGENTS.HANDOFF.TOOL_DESCRIPTION') }}
              </span>
            </a>
            <Switch
              :model-value="handoffLigada"
              :disabled="!isSuperAdmin"
              :title="
                isSuperAdmin ? '' : t('POWER_AGENTS.HANDOFF.SWITCH_LOCKED')
              "
              class="shrink-0"
              @update:model-value="alternarHandoff"
            />
          </div>
          <a
            href="#"
            role="button"
            data-teste="handoff-resumo"
            class="flex flex-wrap items-center px-3 pb-3 text-xs gap-x-2 gap-y-1 text-n-slate-11"
            @click.prevent="abrirHandoff"
          >
            <span class="min-w-0 ps-[42px]">{{ resumoDoHandoff }}</span>
            <span class="font-medium ms-auto text-n-brand">
              {{ t('POWER_AGENTS.HANDOFF.CONFIGURE') }}
            </span>
          </a>
        </div>

        <!-- Atualizar o funil (CRM-03): mesma anatomia do card do humano, e os mesmos
             <a role="button"> pelo mesmo motivo (o fieldset da faixa de leitura). -->
        <div
          v-if="kanbanNaBusca"
          data-busca="kanban-tool"
          class="border rounded-lg border-n-weak"
          :class="kanbanLigada ? '' : 'opacity-60'"
        >
          <div class="flex items-center gap-3 p-3">
            <IconeDaTool />
            <a
              href="#"
              role="button"
              class="flex-1 min-w-0 py-1 text-start"
              @click.prevent="abrirKanban"
            >
              <span class="block text-sm font-medium truncate text-n-slate-12">
                {{ t('POWER_AGENTS.KANBAN.TOOL_NAME') }}
              </span>
              <span class="block text-xs truncate text-n-slate-11">
                {{ t('POWER_AGENTS.KANBAN.TOOL_DESCRIPTION') }}
              </span>
            </a>
            <Switch
              :model-value="kanbanLigada"
              :disabled="!isSuperAdmin"
              :title="
                isSuperAdmin ? '' : t('POWER_AGENTS.KANBAN.SWITCH_LOCKED')
              "
              class="shrink-0"
              @update:model-value="alternarKanban"
            />
          </div>
          <a
            href="#"
            role="button"
            data-teste="kanban-resumo"
            class="flex flex-wrap items-center px-3 pb-3 text-xs gap-x-2 gap-y-1 text-n-slate-11"
            @click.prevent="abrirKanban"
          >
            <span class="min-w-0 ps-[42px]">{{ resumoDoKanban }}</span>
            <span class="font-medium ms-auto text-n-brand">
              {{ t('POWER_AGENTS.KANBAN.CONFIGURE') }}
            </span>
          </a>
        </div>

        <!-- `list-none`: sem ele o navegador põe o marcador de lista ao lado de cada carta, e o
             card de atendimento humano (que é <div>) ficava sem — duas anatomias na mesma lista. -->
        <ul class="flex flex-col gap-2 list-none">
          <!-- A carta é uma LINHA. Ela tinha campo de versão, botão de detalhes, um bloco de
               jargão de runtime e o remover lá dentro — quatro tools enchiam a tela e a pessoa
               rolava para ver o que o agente sabe fazer, que é a pergunta da seção.
               Agora: o ícone, o nome, o que faz e o interruptor. O resto abre no clique. -->
          <li
            v-for="carta in visiveis"
            :key="carta.chave"
            class="border rounded-lg border-n-weak"
            :class="carta.ligada ? '' : 'opacity-60'"
          >
            <div class="flex items-center gap-3 p-3">
              <IconeDaTool
                :flow="carta.forma === 'flow'"
                :http="carta.ferramentaHttp"
              />
              <!-- `px-0`: o `_base.scss` dá `px-2.5` a todo <button>, e o texto ficaria 10 px
                   mais longe do ícone do que o vão da linha. -->
              <button
                type="button"
                class="flex-1 min-w-0 px-0 text-start"
                :disabled="!isSuperAdmin"
                @click="abrirDetalhe(carta)"
              >
                <!-- A versão do flow em texto cinza, sem etiqueta colorida (Murilo, 16/10). O efeito
                     (grava ou só lê) saiu da carta na emenda de 17/10 da FLOW-11. -->
                <span class="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                  <span class="text-sm font-medium truncate text-n-slate-12">
                    {{ carta.titulo }}
                  </span>
                  <template v-if="carta.forma === 'flow'">
                    <span class="font-mono text-xxs text-n-slate-9" data-versao>
                      {{
                        carta.estado === 'draft'
                          ? t('POWER_AGENTS.TOOLS.VERSION_DRAFT')
                          : t('POWER_AGENTS.TOOLS.VERSION', { n: carta.versao })
                      }}
                    </span>
                  </template>
                </span>
                <span
                  v-if="carta.descricao"
                  class="block text-xs truncate text-n-slate-11"
                >
                  {{ carta.descricao }}
                </span>
              </button>
              <!-- Na linha, antes do interruptor, e só para quem monta flow (`montaFlow`: o
                   super-admin e, com a chave POWERFLOW_ADMIN_MONTA_FLOW, o administrador). O link
                   leva à versão que o agente usa, e o editor abre o rascunho mais novo dela, quando
                   há um (Gate 4, 6e: `FlowEditor`, `rascunhoVigente`). -->
              <router-link
                v-if="carta.forma === 'flow' && montaFlow && carta.entradaId"
                class="text-xs font-medium shrink-0 text-n-brand hover:underline"
                data-abrir-flow
                :to="{
                  name: 'powerflow_flow_editor',
                  params: { accountId: ctx.accountId(), id: carta.entradaId },
                }"
              >
                {{ t('POWER_AGENTS.TOOLS.OPEN_IN_EDITOR') }}
              </router-link>
              <Switch
                :model-value="carta.ligada"
                :disabled="
                  !isSuperAdmin ||
                  (carta.forma === 'flow' && carta.estado === 'draft')
                "
                class="shrink-0"
                @update:model-value="alternarLigada(carta)"
              />
            </div>

            <!-- Fica na carta, e não atrás do clique: uma tool depreciada continua respondendo —
                 nada falha e nada loga. Esconder isso seria o silêncio de sempre. É raro, então
                 não engorda o caso comum. -->
            <p
              v-if="carta.estado === 'deprecated'"
              class="px-3 pb-3 text-xs text-n-amber-11"
            >
              {{ t('POWER_AGENTS.TOOLS.CATALOG_DEPRECATED_WARNING') }}
            </p>
          </li>
        </ul>
      </div>

      <!-- `currentPageInfo` é uma CHAVE de i18n, e não o texto: o componente a resolve com
           startItem/endItem/totalItems e com plural. Passar texto pronto imprime a frase com os
           `{startItem}` literais. -->
      <PaginationFooter
        v-if="paginado"
        :current-page="paginaAtual"
        :total-items="filtradas.length"
        :items-per-page="POR_PAGINA"
        current-page-info="POWER_AGENTS.TOOLS.CATALOG_PAGE_INFO"
        @update:current-page="paginaAtual = $event"
      />
    </div>

    <!-- O catálogo deixa de ser um <select> no meio da página. Diálogo com busca: quem
         procura "cep" não sabe que a tool se chama `consultar_cep`, e quem procura "humano"
         não sabe que é a descrição que tem a palavra — por isso a busca olha as duas. -->
    <!-- TOOLS-05 — um caminho só para adicionar, em etapas, no padrão do AddSourceDialog do
         PowerData. O <select> de anexar e o formulário da aba de legado deixaram de ser dois
         caminhos concorrentes. -->
    <AddToolDialog
      ref="dialogoRef"
      :account-id="ctx.accountId()"
      :disponiveis="disponiveis"
      :instancias="instancias"
      @attach="anexarVarias"
      @criar="abrirCriacao"
      @instancias-mudaram="carregarInstancias"
    />

    <!-- O detalhe da tool do catálogo. Sem versão e sem identificador de runtime: os dois
         estavam na carta, ninguém sabia o que fazer com eles, e o `version_spec` continua
         valendo `latest` no servidor para quem não escolhe. -->
    <Dialog
      ref="detalheDialogRef"
      :title="cartaDetalhe?.titulo || ''"
      width="lg"
      :show-confirm-button="false"
      :show-cancel-button="false"
      @close="cartaDetalhe = null"
    >
      <div v-if="cartaDetalhe" class="flex flex-col gap-3">
        <p v-if="cartaDetalhe.descricao" class="text-sm text-n-slate-11">
          {{ cartaDetalhe.descricao }}
        </p>
        <p v-if="cartaDetalhe.notaDeUso" class="text-sm text-n-slate-10">
          {{ cartaDetalhe.notaDeUso }}
        </p>
        <router-link
          v-if="cartaDetalhe.forma === 'template' && cartaDetalhe.instanciaId"
          :to="rotaDaInstancia"
          class="text-sm w-fit text-n-brand hover:underline"
        >
          {{ t('POWER_AGENTS.TOOLS.CATALOG_OPEN_INSTANCE') }}
        </router-link>
        <!-- Remover fica aqui, e não na carta: desligar é reversível e é o que se quer 9 vezes
             em 10; remover apaga a configuração junto. Perto do polegar viram o mesmo gesto. -->
        <button
          class="text-sm w-fit text-n-ruby-11 hover:underline"
          @click="removerDoAgente(cartaDetalhe)"
        >
          {{ t('POWER_AGENTS.TOOLS.CARD_REMOVE') }}
        </button>
      </div>
    </Dialog>

    <!-- O formulário REAPROVEITADO: é o mesmo `ToolsCrud`, com as listas escondidas. Ele já traz
         importar cURL, dry-run, parâmetro preenchido pelo agente ou fixo, token de variável e
         rascunho com o segredo redigido — escrever um segundo formulário faria metade disso. -->
    <Dialog
      ref="formDialogRef"
      :title="
        cartaEmEdicao
          ? t('POWER_AGENTS.TOOLS.EDIT_TITLE', { name: cartaEmEdicao.titulo })
          : t('POWER_AGENTS.TOOLS.ADD_CUSTOM')
      "
      width="2xl"
      :show-confirm-button="false"
      :show-cancel-button="false"
      overflow-y-auto
      @close="cartaEmEdicao = null"
    >
      <ToolsCrud
        ref="formRef"
        apenas-formulario
        :bindings="bindings"
        :available-tools="availableTools"
        :on-test="runTest"
        :on-submit-custom="bindCustom"
        :on-update-tool="updateTool"
        :draft-key="draftKey"
        :variables="toolVariables"
        :segredos="gestorDoCofre ? nomesDoCofre : []"
        @delete-tool="deleteTool"
      />
    </Dialog>

    <!-- A configuração de Pedir atendimento humano, que até 22/09/2026 era a aba Atendimento
         humano. O Salvar é do diálogo: o formulário não sabe em que moldura está. O conteúdo
         só monta ao abrir, então cada abertura parte do agente como ele está agora. -->
    <Dialog
      ref="handoffDialogRef"
      :title="t('POWER_AGENTS.HANDOFF.TOOL_NAME')"
      :description="t('POWER_AGENTS.HANDOFF.DIALOG_DESCRIPTION')"
      width="2xl"
      overflow-y-auto
      :confirm-button-label="t('POWER_AGENTS.SAVE')"
      :show-confirm-button="!somenteLeitura"
      :is-loading="handoffFormRef?.saving"
      @confirm="handoffFormRef?.save()"
    >
      <HandoffForm
        ref="handoffFormRef"
        :agent="agent"
        :teams="teams"
        @saved="handoffDialogRef?.close()"
      />
    </Dialog>

    <!-- A configuração de Atualizar o funil (CRM-03). Mesmo molde do diálogo acima: o Salvar
         é do diálogo e o conteúdo só monta ao abrir. -->
    <Dialog
      v-if="kanbanNaConta"
      ref="kanbanDialogRef"
      :title="t('POWER_AGENTS.KANBAN.TOOL_NAME')"
      :description="t('POWER_AGENTS.KANBAN.DIALOG_DESCRIPTION')"
      width="2xl"
      overflow-y-auto
      :confirm-button-label="t('POWER_AGENTS.SAVE')"
      :show-confirm-button="!somenteLeitura"
      :is-loading="kanbanFormRef?.saving"
      @confirm="kanbanFormRef?.save()"
    >
      <KanbanToolForm
        ref="kanbanFormRef"
        :agent="agent"
        @saved="kanbanDialogRef?.close()"
      />
    </Dialog>
  </SectionShell>
</template>
